export type RedditFeedPost = {
  title: string;
  link: string;
  subreddit: string;
};

/** Built-in filters (megathreads + common noisy domains). */
export const DEFAULT_REDDIT_BLOCKLIST_PATTERNS = [
  '(?i)megathread',
  '(?i)live thread',
  '(?i)discussion thread',
  '^/r/',
  '(?i)thedailybeast',
  '(?i)newsweek',
  '(?i)huffpost',
  '(?i)breitbart',
] as const;

export type CompiledBlocklist = {
  patterns: RegExp[];
  invalidLines: string[];
};

export function parseBlocklistLines(raw: string): string[] {
  return raw
    .split('\n')
    .map(line => line.trim())
    .filter(line => line.length > 0 && !line.startsWith('#'));
}

export function compileBlocklistPatterns(lines: readonly string[]): CompiledBlocklist {
  const patterns: RegExp[] = [];
  const invalidLines: string[] = [];
  for (const line of lines) {
    try {
      patterns.push(lineToRegExp(line));
    } catch {
      invalidLines.push(line);
    }
  }
  return { patterns, invalidLines };
}

/** Supports leading (?i) for case-insensitive match (PCRE-style, not native in JS). */
export function lineToRegExp(line: string): RegExp {
  let pattern = line;
  let flags = '';
  if (pattern.startsWith('(?i)')) {
    flags = 'i';
    pattern = pattern.slice(4);
  }
  return new RegExp(pattern, flags);
}

export function isMegathreadOrMetaTitle(title: string): boolean {
  const t = title.trim();
  if (t.startsWith('/r/')) return true;
  if (/megathread/i.test(t)) return true;
  if (/live thread/i.test(t)) return true;
  if (/discussion thread/i.test(t)) return true;
  return false;
}

/** True if title or link URL matches any blocklist regex. */
export function postMatchesBlocklist(
  post: Pick<RedditFeedPost, 'title' | 'link'>,
  patterns: readonly RegExp[]
): boolean {
  const haystack = `${post.title}\n${post.link}`;
  return patterns.some(p => p.test(haystack) || p.test(hostnameFromUrl(post.link)));
}

export function filterRedditPosts(
  posts: readonly RedditFeedPost[],
  patterns: readonly RegExp[]
): RedditFeedPost[] {
  return posts.filter(p => {
    if (isMegathreadOrMetaTitle(p.title)) return false;
    if (postMatchesBlocklist(p, patterns)) return false;
    return true;
  });
}

function hostnameFromUrl(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}
